import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, ShoppingCart, Plus, Minus, X, Grid3X3, List, Filter, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { gramsOf, normalizeGrams, formatGrams, WEIGHT_PRESETS } from '../lib/cartUnits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Main categories structure
const MAIN_CATEGORIES = ['Fruits', 'Vegetables', 'Herbs & Specialty'];

export default function ShopPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeMainCategory, setActiveMainCategory] = useState(searchParams.get('main') || 'all');
    const [activeSubCategory, setActiveSubCategory] = useState(searchParams.get('sub') || 'all');
    const [viewMode, setViewMode] = useState('grid');
    const { cart, addToCart, getItemQuantity } = useCart();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const productsRes = await axios.get(`${API}/products`, { withCredentials: true });
                setProducts(productsRes.data);
            } catch (error) {
                console.error('Error fetching products:', error);
                if (error.response?.status === 403) {
                    toast.error('Please verify your email address to view products and prices.');
                } else {
                    toast.error('Failed to load products');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Get subcategories for the active main category
    const subCategories = useMemo(() => {
        if (activeMainCategory === 'all') {
            // Get all unique subcategories
            const allSubs = [...new Set(products.map(p => p.sub_category))].filter(Boolean).sort();
            return allSubs;
        }
        const subs = [...new Set(
            products
                .filter(p => p.main_category === activeMainCategory)
                .map(p => p.sub_category)
        )].filter(Boolean).sort();
        return subs;
    }, [products, activeMainCategory]);

    // Filter products based on selections
    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            const matchesMainCategory = activeMainCategory === 'all' || product.main_category === activeMainCategory;
            const matchesSubCategory = activeSubCategory === 'all' || product.sub_category === activeSubCategory;
            const matchesSearch = !searchQuery || 
                product.name.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesMainCategory && matchesSubCategory && matchesSearch;
        });
    }, [products, activeMainCategory, activeSubCategory, searchQuery]);

    // Count products per main category
    const mainCategoryCounts = useMemo(() => {
        const counts = { all: products.length };
        MAIN_CATEGORIES.forEach(cat => {
            counts[cat] = products.filter(p => p.main_category === cat).length;
        });
        return counts;
    }, [products]);

    // Count products per subcategory
    const subCategoryCounts = useMemo(() => {
        const counts = {};
        const relevantProducts = activeMainCategory === 'all' 
            ? products 
            : products.filter(p => p.main_category === activeMainCategory);
        
        relevantProducts.forEach(p => {
            if (p.sub_category) {
                counts[p.sub_category] = (counts[p.sub_category] || 0) + 1;
            }
        });
        return counts;
    }, [products, activeMainCategory]);

    const handleMainCategoryChange = (category) => {
        setActiveMainCategory(category);
        setActiveSubCategory('all'); // Reset subcategory when main category changes
        
        if (category === 'all') {
            searchParams.delete('main');
        } else {
            searchParams.set('main', category);
        }
        searchParams.delete('sub');
        setSearchParams(searchParams);
    };

    const handleSubCategoryChange = (category) => {
        setActiveSubCategory(category);
        
        if (category === 'all') {
            searchParams.delete('sub');
        } else {
            searchParams.set('sub', category);
        }
        setSearchParams(searchParams);
    };

    const handleAddToCart = async (product, delta) => {
        const currentQty = getItemQuantity(product.product_id);
        const newQty = Math.max(0, currentQty + delta);
        
        try {
            await addToCart(product.product_id, newQty);
            if (newQty > currentQty) {
                toast.success(`Added ${product.name}`, {
                    duration: 1500,
                    position: 'bottom-center'
                });
            }
        } catch (error) {
            toast.error('Failed to update cart');
        }
    };

    const handleSetWeight = async (product, grams) => {
        try {
            if (!grams || grams <= 0) {
                await addToCart(product.product_id, 0);
                toast.success('Item removed', { duration: 1500, position: 'bottom-center' });
            } else {
                await addToCart(product.product_id, grams / 1000);
                toast.success(`Added ${formatGrams(grams)} ${product.name}`, { duration: 1500, position: 'bottom-center' });
            }
        } catch (error) {
            toast.error('Failed to update cart');
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-24 md:pb-8">
            {/* Header */}
            <div className="sticky top-16 z-30 bg-white border-b border-slate-100 shadow-sm">
                <div className="container-main py-4">
                    {/* Search and View Toggle */}
                    <div className="flex gap-3 mb-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <Input
                                data-testid="search-input"
                                type="text"
                                placeholder="Search products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full"
                                    data-testid="clear-search-btn"
                                >
                                    <X className="w-4 h-4 text-slate-400" />
                                </button>
                            )}
                        </div>
                        {/* View Toggle */}
                        <div className="flex bg-slate-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-md transition-colors ${
                                    viewMode === 'grid' 
                                        ? 'bg-white text-[#1B4D3E] shadow-sm' 
                                        : 'text-slate-500 hover:text-slate-700'
                                }`}
                                data-testid="grid-view-btn"
                                title="Grid view"
                            >
                                <Grid3X3 className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md transition-colors ${
                                    viewMode === 'list' 
                                        ? 'bg-white text-[#1B4D3E] shadow-sm' 
                                        : 'text-slate-500 hover:text-slate-700'
                                }`}
                                data-testid="list-view-btn"
                                title="List view"
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Main Category Tabs */}
                    <div className="flex gap-2 mb-3">
                        <button
                            onClick={() => handleMainCategoryChange('all')}
                            data-testid="main-category-all"
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                activeMainCategory === 'all'
                                    ? 'bg-[#1B4D3E] text-white shadow-md'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                        >
                            All Products
                            <span className="ml-1.5 text-xs opacity-75">({mainCategoryCounts.all})</span>
                        </button>
                        {MAIN_CATEGORIES.map((category) => (
                            <button
                                key={category}
                                onClick={() => handleMainCategoryChange(category)}
                                data-testid={`main-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                    activeMainCategory === category
                                        ? 'bg-[#1B4D3E] text-white shadow-md'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                            >
                                {category}
                                <span className="ml-1.5 text-xs opacity-75">({mainCategoryCounts[category] || 0})</span>
                            </button>
                        ))}
                    </div>

                    {/* Subcategory Filter */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Filter className="w-4 h-4" />
                            <span>Filter by:</span>
                        </div>
                        <Select value={activeSubCategory} onValueChange={handleSubCategoryChange}>
                            <SelectTrigger className="w-[200px]" data-testid="subcategory-filter">
                                <SelectValue placeholder="All Subcategories" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Subcategories</SelectItem>
                                {subCategories.map((subCat) => (
                                    <SelectItem key={subCat} value={subCat}>
                                        {subCat} ({subCategoryCounts[subCat] || 0})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        {/* Quick subcategory buttons for desktop */}
                        <ScrollArea className="hidden md:block flex-1">
                            <div className="flex gap-2 pb-2">
                                {subCategories.slice(0, 8).map((subCat) => (
                                    <button
                                        key={subCat}
                                        onClick={() => handleSubCategoryChange(subCat)}
                                        data-testid={`sub-category-${subCat.toLowerCase().replace(/\s+/g, '-')}`}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                                            activeSubCategory === subCat
                                                ? 'bg-green-100 text-green-800 ring-1 ring-green-300'
                                                : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                                        }`}
                                    >
                                        {subCat}
                                    </button>
                                ))}
                            </div>
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </div>
                </div>
            </div>

            {/* Products */}
            <div className="container-main py-6">
                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {[...Array(10)].map((_, i) => (
                            <div key={i} className="product-card animate-pulse">
                                <div className="aspect-square bg-slate-200 rounded-xl mb-3" />
                                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                                <div className="h-3 bg-slate-200 rounded w-1/2" />
                            </div>
                        ))}
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-10 h-10 text-slate-300" />
                        </div>
                        <h3 className="text-lg font-medium text-slate-700 mb-2">No products found</h3>
                        <p className="text-slate-500 mb-4">
                            {searchQuery 
                                ? `No results for "${searchQuery}"`
                                : 'No products in this category'
                            }
                        </p>
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setSearchQuery('');
                                setActiveMainCategory('all');
                                setActiveSubCategory('all');
                            }}
                        >
                            Clear Filters
                        </Button>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {filteredProducts.map((product) => {
                            const cartItem = cart.items?.find(i => i.product_id === product.product_id);
                            const isWeight = product.unit === 'kg';
                            const currentGrams = isWeight && cartItem ? gramsOf(cartItem) : 0;
                            return (
                                <ProductCard 
                                    key={product.product_id} 
                                    product={product}
                                    quantity={getItemQuantity(product.product_id)}
                                    isWeight={isWeight}
                                    currentGrams={currentGrams}
                                    onAdd={() => handleAddToCart(product, 1)}
                                    onRemove={() => handleAddToCart(product, -1)}
                                    onSetWeight={(g) => handleSetWeight(product, g)}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredProducts.map((product) => {
                            const cartItem = cart.items?.find(i => i.product_id === product.product_id);
                            const isWeight = product.unit === 'kg';
                            const currentGrams = isWeight && cartItem ? gramsOf(cartItem) : 0;
                            return (
                                <ProductListItem 
                                    key={product.product_id} 
                                    product={product}
                                    quantity={getItemQuantity(product.product_id)}
                                    isWeight={isWeight}
                                    currentGrams={currentGrams}
                                    onAdd={() => handleAddToCart(product, 1)}
                                    onRemove={() => handleAddToCart(product, -1)}
                                    onSetWeight={(g) => handleSetWeight(product, g)}
                                />
                            );
                        })}
                    </div>
                )}
                
                {/* Results count */}
                {!loading && filteredProducts.length > 0 && (
                    <div className="text-center mt-8 text-sm text-slate-500">
                        Showing {filteredProducts.length} of {products.length} products
                    </div>
                )}
            </div>

            {/* Mobile Cart Button */}
            {cart.items?.length > 0 && (
                <div className="fixed bottom-4 left-4 right-4 md:hidden z-50">
                    <Link to="/cart">
                        <Button className="w-full bg-[#1B4D3E] hover:bg-[#163D32] text-white py-6 rounded-xl shadow-lg">
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            View Cart ({cart.item_count} items)
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
}

function WeightControl({ product, currentGrams, onSet }) {
    const [grams, setGrams] = useState(currentGrams > 0 ? currentGrams : 500);
    const [custom, setCustom] = useState('');
    useEffect(() => { if (currentGrams > 0) setGrams(currentGrams); }, [currentGrams]);

    const effective = custom !== '' ? (Number(custom) || 0) : grams;
    const livePrice = ((effective / 1000) * (product.price || 0));
    const inCart = currentGrams > 0;

    const pick = (g) => { setCustom(''); setGrams(g); };
    const commit = () => {
        const g = normalizeGrams(effective);
        setCustom('');
        setGrams(g);
        onSet(g);
    };

    return (
        <div className="mt-3 space-y-2" data-testid={`weight-control-${product.product_id}`}>
            <div className="grid grid-cols-3 gap-1">
                {WEIGHT_PRESETS.map((g) => (
                    <button
                        key={g}
                        type="button"
                        onClick={() => pick(g)}
                        className={`text-xs font-semibold py-1.5 rounded-md border transition-colors ${grams === g && custom === '' ? 'bg-[#1B4D3E] text-white border-[#1B4D3E]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4D3E]'}`}
                        data-testid={`weight-preset-${g}-${product.product_id}`}
                    >
                        {formatGrams(g)}
                    </button>
                ))}
            </div>
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <input
                        type="number"
                        inputMode="numeric"
                        min="100"
                        step="50"
                        placeholder="grams"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
                        className="w-full text-sm border border-slate-200 rounded-md pl-2 pr-6 py-1.5 focus:outline-none focus:border-[#1B4D3E]"
                        data-testid={`weight-custom-${product.product_id}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">g</span>
                </div>
                <span className="text-[#1B4D3E] font-semibold text-sm whitespace-nowrap" data-testid={`weight-price-${product.product_id}`}>
                    ${livePrice.toFixed(2)}
                </span>
            </div>
            <p className="text-[10px] text-slate-400 leading-tight">
                Min 100g · rounds to 50g · ${(product.price || 0).toFixed(2)}/kg
            </p>
            <Button
                onClick={commit}
                className={inCart ? 'w-full bg-[#1B4D3E] text-white hover:bg-[#163D32]' : 'w-full border border-[#1B4D3E] bg-white text-[#1B4D3E] hover:bg-[#1B4D3E] hover:text-white'}
                data-testid={`weight-add-btn-${product.product_id}`}
            >
                {inCart ? `Update · ${formatGrams(currentGrams)} in cart` : `Add · ${formatGrams(normalizeGrams(effective))}`}
            </Button>
            {inCart && (
                <button
                    type="button"
                    onClick={() => onSet(0)}
                    className="w-full text-xs text-red-500 hover:text-red-600"
                    data-testid={`weight-remove-${product.product_id}`}
                >
                    Remove from cart
                </button>
            )}
        </div>
    );
}

// Product Card Component (Grid View)
function ProductCard({ product, quantity, onAdd, onRemove, isWeight, currentGrams, onSetWeight }) {
    const [imgError, setImgError] = useState(false);
    const inCart = isWeight ? currentGrams > 0 : quantity > 0;
    
    return (
        <div className="product-card group" data-testid={`product-${product.product_id}`}>
            <div className="relative aspect-square mb-3 rounded-xl overflow-hidden bg-slate-100">
                {product.image_url && !imgError ? (
                    <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={() => setImgError(true)}
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
                        <span className="text-4xl">🥬</span>
                    </div>
                )}
                
                {/* Quick add button overlay */}
                {!inCart && (
                    <button
                        onClick={isWeight ? () => onSetWeight(500) : onAdd}
                        className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                        data-testid={`quick-add-${product.product_id}`}
                    >
                        <span className="bg-white rounded-full p-3 shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
                            <Plus className="w-5 h-5 text-[#1B4D3E]" />
                        </span>
                    </button>
                )}
            </div>

            <div className="space-y-1">
                <h3 className="font-medium text-slate-800 text-sm line-clamp-2 leading-tight">
                    {product.name}
                </h3>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs bg-slate-50">
                            {product.unit}
                        </Badge>
                        <span className="text-xs text-slate-400">{product.sub_category}</span>
                    </div>
                </div>
                {product.price && (
                    <div className="text-[#1B4D3E] font-semibold text-lg">
                        ${product.price.toFixed(2)}
                    </div>
                )}
            </div>

            {/* Amount controls */}
            {isWeight ? (
                <WeightControl product={product} currentGrams={currentGrams} onSet={onSetWeight} />
            ) : quantity > 0 ? (
                <div className="flex items-center justify-between mt-3 bg-[#1B4D3E] rounded-lg p-1">
                    <button
                        onClick={onRemove}
                        className="p-2 text-white hover:bg-white/10 rounded-md transition-colors"
                        data-testid={`remove-${product.product_id}`}
                    >
                        <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-white font-medium" data-testid={`qty-${product.product_id}`}>
                        {quantity}
                    </span>
                    <button
                        onClick={onAdd}
                        className="p-2 text-white hover:bg-white/10 rounded-md transition-colors"
                        data-testid={`add-${product.product_id}`}
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <Button
                    onClick={onAdd}
                    variant="outline"
                    className="w-full mt-3 border-[#1B4D3E] text-[#1B4D3E] hover:bg-[#1B4D3E] hover:text-white"
                    data-testid={`add-btn-${product.product_id}`}
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                </Button>
            )}
        </div>
    );
}

// Product List Item Component (List View)
function ProductListItem({ product, quantity, onAdd, onRemove, isWeight, currentGrams, onSetWeight }) {
    const [imgError, setImgError] = useState(false);
    
    return (
        <div 
            className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
            data-testid={`product-list-${product.product_id}`}
        >
            {/* Image */}
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                {product.image_url && !imgError ? (
                    <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={() => setImgError(true)}
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
                        <span className="text-2xl">🥬</span>
                    </div>
                )}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-800 truncate">{product.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs bg-slate-50">
                        {product.unit}
                    </Badge>
                    <span className="text-xs text-slate-400">{product.sub_category}</span>
                    {product.price && (
                        <span className="text-[#1B4D3E] font-semibold ml-auto">
                            ${product.price.toFixed(2)}
                        </span>
                    )}
                </div>
            </div>

            {/* Amount controls */}
            {isWeight ? (
                <div className="w-56 shrink-0">
                    <WeightControl product={product} currentGrams={currentGrams} onSet={onSetWeight} />
                </div>
            ) : quantity > 0 ? (
                <div className="flex items-center gap-2 bg-[#1B4D3E] rounded-lg p-1">
                    <button
                        onClick={onRemove}
                        className="p-2 text-white hover:bg-white/10 rounded-md transition-colors"
                    >
                        <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-white font-medium w-8 text-center">{quantity}</span>
                    <button
                        onClick={onAdd}
                        className="p-2 text-white hover:bg-white/10 rounded-md transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <Button
                    onClick={onAdd}
                    variant="outline"
                    size="sm"
                    className="border-[#1B4D3E] text-[#1B4D3E] hover:bg-[#1B4D3E] hover:text-white"
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                </Button>
            )}
        </div>
    );
}
